import { Button } from '@ui';
import { useRef } from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { cn } from '~/lib/cn';
import { COLOR_PRESETS, DEFAULT_PRESET, isHexColor, isPreset, sameColor } from './colorPalette';
import { FormInputProps } from './props';

interface SwatchProps {
    color: string;
    selected: boolean;
    label: string;
    onSelect: () => void;
}

const Swatch = ({ color, selected, label, onSelect }: SwatchProps) => (
    <button
        type="button"
        aria-label={label}
        aria-pressed={selected}
        className={cn(
            'tw-flex tw-size-7 tw-items-center tw-justify-center tw-rounded-full tw-border-2 tw-border-solid tw-transition-transform hover:tw-scale-110 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-accents-9',
            selected ? 'tw-border-accents-9 tw-ring-2 tw-ring-accents-5 tw-ring-offset-1' : 'tw-border-white/80'
        )}
        style={{ backgroundColor: color }}
        onClick={onSelect}
    />
);

interface FormColorPickerProps extends FormInputProps {
    className?: string;
}

export const FormColorPicker = ({ className, name, label }: FormColorPickerProps) => {
    const { t } = useTranslation('form');
    const colorRef = useRef<HTMLInputElement>(null);
    const {
        field: { value, onChange, onBlur, name: fieldName }
    } = useController({ name });
    const colorValue = typeof value === 'string' ? value : '';

    /**
     * A colour from outside the palette gets a swatch of its own, so that a
     * record carrying one — set by a colleague, or mixed here a moment ago —
     * still shows something as selected rather than nothing at all.
     */
    const custom = isHexColor(colorValue) && !isPreset(colorValue) ? colorValue : null;

    return (
        <div className={cn('tw-relative tw-my-3', className)} role="group" aria-label={label}>
            {label && <span className="tw-mb-1 tw-block tw-pl-1 tw-text-sm tw-font-normal">{label}</span>}
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                {COLOR_PRESETS.map((preset) => (
                    <Swatch
                        key={preset}
                        color={preset}
                        selected={sameColor(colorValue, preset)}
                        label={t('colorPicker.preset', { color: preset })}
                        onSelect={() => onChange(preset)}
                    />
                ))}
                {custom && (
                    <Swatch
                        color={custom}
                        selected
                        label={t('colorPicker.preset', { color: custom })}
                        onSelect={() => onChange(custom)}
                    />
                )}
            </div>
            <div className="tw-mt-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <Button size="sm" color="secondary" onPress={() => colorRef.current?.click()}>
                    {t('colorPicker.custom')}
                </Button>
                <Button flat size="sm" color="secondary" onPress={() => onChange('')}>
                    {t('colorPicker.none')}
                </Button>
            </div>
            <input
                type="color"
                ref={colorRef}
                className="tw-sr-only"
                onBlur={onBlur}
                name={fieldName}
                onChange={(e) => onChange(e.target.value)}
                value={colorValue || DEFAULT_PRESET}
            />
        </div>
    );
};
